import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import PhygitalIcon from "@/components/PhygitalIcon";
import EarlyAccessModal from "@/components/EarlyAccessModal";
import SharedFooter from "@/components/SharedFooter";
import { EditableText, EditableImage, EditableSection, useAdmin } from "@/components/AdminContentEditor";
import PageMeta from "@/components/PageMeta";
import axios from "axios";
import {
  ArrowRight,
  Check,
  Eye,
  Share,
  Calendar,
  Users,
  FolderOpen,
  Bell,
  Image,
  Sparkles,
  Link2,
  LayoutDashboard,
  Briefcase,
  MessageCircle,
  ImageIcon,
  Scissors,
  Palette,
  Heart,
  ShoppingBag,
  PartyPopper,
  Plane,
  Cannabis
} from "lucide-react";
import { getCategoryPreset } from "../lib/categoryConfig";

const API = process.env.REACT_APP_BACKEND_URL || "";

// Demo navigation items - routes, descriptions, groupings. Colors/icons come from CATEGORY_PRESETS.
const DEMO_NAV_ITEMS = [
  { group: "For Providers", items: [
    { catKey: "hair", path: "/hair", desc: "Hair stylists" },
    { catKey: "makeup", path: "/mua", desc: "Makeup artists" },
    { catKey: "nails", path: "/nail", desc: "Nail technicians" },
    { catKey: "wellness", path: "/wellness", desc: "Wellness providers" },
    { catKey: "instructors", path: "/instructor", desc: "Educators & trainers" },
    { catKey: "culinary", path: "/culinary", desc: "Private chefs & bakers" },
  ]},
  { group: "For Hosts", items: [
    { catKey: "events", path: "/events", desc: "Event hosts" },
  ]},
  { group: "Coming Soon", items: [
    { catKey: "travel", path: "/travel", desc: "Travel planners", soon: true },
    { catKey: "dispensary", path: "/dispensary", desc: "Cannabis retail", soon: true },
    { catKey: "style", path: "/retail", desc: "Personal stylists", soon: true },
  ]},
];

// Animated word cycle for headline
const ROTATING_WORDS = [
  { word: "hair", tagline: "Your hair preferences, perfectly communicated" },
  { word: "makeup", tagline: "Your makeup preferences, perfectly communicated" },
  { word: "nail", tagline: "Your nail preferences, perfectly communicated" },
  { word: "lash", tagline: "Your lash preferences, perfectly communicated" },
  { word: "event", tagline: "Your event preferences, perfectly communicated" }
];

const HomePage = () => {
  const navigate = useNavigate();
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showDemoDropdown, setShowDemoDropdown] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    return !!localStorage.getItem("session_token");
  });
  const { editMode } = useAdmin();
  
  // Listen for storage changes (logout from other tabs/components)
  useEffect(() => {
    const handleStorageChange = () => {
      setIsLoggedIn(!!localStorage.getItem("session_token"));
    };
    
    // Also check on window focus (for same-tab logout)
    const handleFocus = () => {
      setIsLoggedIn(!!localStorage.getItem("session_token"));
    };
    
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("focus", handleFocus);
    
    // Check periodically for token changes
    const interval = setInterval(handleFocus, 1000);
    
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("focus", handleFocus);
      clearInterval(interval);
    };
  }, []);
  
  // Early Access Modal State
  const [showEarlyAccessModal, setShowEarlyAccessModal] = useState(false);

  useEffect(() => {
    document.title = "phygital - Your preferences, perfectly communicated";
  }, []);

  useEffect(() => {    
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentWordIndex((prev) => (prev + 1) % ROTATING_WORDS.length);
        setIsAnimating(false);
      }, 300);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  const currentItem = ROTATING_WORDS[currentWordIndex];

  return (
    <div className="min-h-screen bg-white">
      <PageMeta 
        title="Phygital - Your Preferences, Perfectly Communicated"
        description="Create a digital profile of your preferences. Share your style, accessibility needs, and more with any service provider."
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-50" />
        
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-16">
          {/* Nav */}
          <nav className="flex items-center justify-between mb-12">
            <div className="flex items-center gap-2 sm:gap-3">
              <PhygitalIcon className="w-9 h-9 sm:w-10 sm:h-10" variant="light" />
              <span className="font-semibold text-xl sm:text-2xl">phygital</span>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Demo Dropdown - Desktop */}
              <div className="hidden md:block relative">
                <button 
                  onClick={() => setShowDemoDropdown(!showDemoDropdown)}
                  onBlur={() => setTimeout(() => setShowDemoDropdown(false), 200)}
                  className="flex items-center gap-1 text-gray-700 hover:text-gray-900 font-medium text-sm px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <Eye className="w-4 h-4" />
                  <span>Demos</span>
                  <svg className={`w-4 h-4 transition-transform ${showDemoDropdown ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showDemoDropdown && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-50 max-h-[calc(100vh-120px)] overflow-y-auto">
                    <div className="p-1.5">
                      {DEMO_NAV_ITEMS.map((group, gi) => (
                        <div key={gi}>
                          {gi > 0 && <div className="border-t border-gray-100 my-1" />}
                          <p className="text-[10px] text-gray-500 uppercase tracking-wide px-3 py-1">{group.group}</p>
                          {group.items.map(item => {
                            const preset = getCategoryPreset(item.catKey);
                            const Icon = preset?.icon || Sparkles;
                            return (
                              <Link key={item.catKey} to={item.path} className="flex items-center gap-2 px-2.5 py-1.5 hover:bg-gray-50 rounded-md transition-colors">
                                <div className="w-6 h-6 rounded flex items-center justify-center" style={{ backgroundColor: preset?.bgColor }}>
                                  <Icon className="w-4 h-4" style={{ color: preset?.iconColor }} />
                                </div>
                                <div className="flex-1">
                                  <p className="font-medium text-sm text-gray-900">{preset?.name} Demo</p>
                                  <p className="text-xs text-gray-600">{item.desc}</p>
                                </div>
                                {item.soon && <span className="text-[9px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-medium">SOON</span>}
                              </Link>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {isLoggedIn ? (
                <Button 
                  onClick={() => navigate('/me')}
                  className="rounded-full bg-black hover:bg-gray-800 text-sm px-4 sm:px-6"
                  data-testid="dashboard-btn"
                >
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  Dashboard
                </Button>
              ) : (
                <>
                  <Link to="/login" className="text-gray-700 hover:text-gray-900 font-medium text-sm hidden sm:block">
                    Sign In
                  </Link>
                  <Button 
                    onClick={() => navigate('/login?mode=signup')}
                    className="rounded-full bg-black hover:bg-gray-800 text-sm px-4 sm:px-6"
                  >
                    Get Started
                  </Button>
                </>
              )}
            </div>
          </nav>

          {/* Hero Content */}
          <EditableSection sectionId="home-hero" label="Hero Section" className={editMode ? "p-4" : ""}>
            <div className="text-center max-w-3xl mx-auto">
              <h1 className="text-[26px] sm:text-4xl md:text-5xl font-display font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
                Your{" "}
                <span 
                  className={`inline-block transition-all duration-300 bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent ${
                    isAnimating ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'
                  }`}
                >
                  {currentItem.word}
                </span>
                {" "}preferences,
                <br />
                <span className="block mt-1">perfectly communicated</span>
              </h1>

              <p className="text-[15px] sm:text-base md:text-lg text-gray-700 max-w-2xl mx-auto mb-6 sm:mb-8 px-2 sm:px-4">
                <EditableText
                  sectionId="home-hero"
                  field="tagline"
                  defaultValue="Store your preferences once. Share with anyone, anywhere."
                  className="inline"
                />
              </p>

              {/* CTAs */}
              <div className="flex flex-row gap-3 justify-center max-w-md mx-auto mb-6">
                <Button 
                  onClick={() => navigate('/login?type=customer')}
                  className="flex-1 h-12 sm:h-14 text-sm sm:text-base bg-black hover:bg-gray-800 text-white rounded-full"
                >
                  <Share className="w-4 h-4 mr-2" />
                  <EditableText
                    sectionId="home-hero"
                    field="cta_primary"
                    defaultValue="Store and Share"
                    className="inline"
                  />
                </Button>
                <Button 
                  onClick={() => setShowEarlyAccessModal(true)}
                  variant="outline"
                  className="flex-1 h-12 sm:h-14 text-sm sm:text-base rounded-full border-2 border-gray-300 hover:border-gray-400"
                  data-testid="for-providers-btn"
                >
                  <Briefcase className="w-4 h-4 mr-2" />
                  <EditableText
                    sectionId="home-hero"
                    field="cta_secondary"
                    defaultValue="I Provide Services"
                    className="inline"
                  />
                </Button>
              </div>

              {/* Trust badges */}
              <div className="flex items-center justify-center gap-4 text-xs sm:text-sm text-gray-600">
                <span className="flex items-center gap-1">
                  <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-500" />
                  <EditableText sectionId="home-hero" field="badge_1" defaultValue="Free forever" className="inline" />
                </span>
                <span className="flex items-center gap-1">
                  <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-500" />
                  <EditableText sectionId="home-hero" field="badge_2" defaultValue="No app needed" className="inline" />
                </span>
                <span className="flex items-center gap-1 whitespace-nowrap">
                  <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-500" />
                  <EditableText sectionId="home-hero" field="badge_3" defaultValue="Works anywhere" className="inline" />
                </span>
              </div>
            </div>
          </EditableSection>

          {/* Stacked Preference Cards Preview */}
          <div className="mt-10 sm:mt-14 max-w-sm mx-auto relative h-64">
            {/* Event Card - Back */}
            <div className="absolute left-1/2 top-0 -translate-x-1/2 w-64 rotate-6 transition-all hover:rotate-0 hover:scale-105 z-10">
              <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl p-4 shadow-lg">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 rounded bg-white/20 flex items-center justify-center">
                    <PartyPopper className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-white font-semibold text-sm">Event Preferences</span>
                </div>
                <div className="space-y-1.5 text-white/90 text-xs">
                  <div className="bg-white/20 rounded-lg px-2 py-1">Dietary: Vegan, nut-free</div>
                  <div className="bg-white/20 rounded-lg px-2 py-1">Accessibility: Wheelchair</div>
                </div>
              </div>
            </div>
            
            {/* Nails Card - Middle */}
            <div className="absolute left-1/2 top-8 -translate-x-1/2 w-64 -rotate-3 transition-all hover:rotate-0 hover:scale-105 z-20">
              <div className="bg-gradient-to-br from-pink-500 to-purple-500 rounded-2xl p-4 shadow-lg">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 rounded bg-white/20 flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-white font-semibold text-sm">Nail Preferences</span>
                </div>
                <div className="space-y-1.5 text-white/90 text-xs">
                  <div className="bg-white/20 rounded-lg px-2 py-1">Shape: Almond, medium</div>
                  <div className="bg-white/20 rounded-lg px-2 py-1">Style: Gel, French tips</div>
                </div>
              </div>
            </div>
            
            {/* Hair Card - Front */}
            <div className="absolute left-1/2 top-16 -translate-x-1/2 w-64 rotate-2 transition-all hover:rotate-0 hover:scale-105 z-30">
              <div className="bg-gradient-to-br from-blue-500 to-teal-500 rounded-2xl p-4 shadow-lg">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 rounded bg-white/20 flex items-center justify-center">
                    <Scissors className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-white font-semibold text-sm">Hair Preferences</span>
                </div>
                <div className="space-y-1.5 text-white/90 text-xs">
                  <div className="bg-white/20 rounded-lg px-2 py-1">Knotless braids, bob length, edges</div>
                  <div className="bg-white/20 rounded-lg px-2 py-1 flex items-center gap-1">
                    <MessageCircle className="w-3 h-3" />
                    Tired today, not chatty
                  </div>
                  <div className="bg-white/20 rounded-lg px-2 py-1 flex items-center gap-1">
                    <ImageIcon className="w-3 h-3" />
                    Inspo pic attached
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <EditableSection sectionId="home-how-it-works" label="How It Works" className={editMode ? "py-12 sm:py-16 bg-gray-50" : "py-12 sm:py-16 bg-gray-50"}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-display font-bold text-gray-900 mb-8 sm:mb-10 text-center">
            <EditableText sectionId="home-how-it-works" field="title" defaultValue="How it works" />
          </h2>

          <div className="grid grid-cols-3 gap-3 sm:gap-6 max-w-3xl mx-auto">
            <div className="text-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-black flex items-center justify-center mx-auto mb-2 sm:mb-3">
                <span className="text-base sm:text-lg font-bold text-white">1</span>
              </div>
              <h3 className="font-semibold text-sm sm:text-base mb-1">
                <EditableText sectionId="home-how-it-works" field="step1_title" defaultValue="Set up" />
              </h3>
              <p className="text-gray-700 text-xs sm:text-sm">
                <EditableText sectionId="home-how-it-works" field="step1_desc" defaultValue="Your preferences" />
              </p>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-black flex items-center justify-center mx-auto mb-2 sm:mb-3">
                <span className="text-base sm:text-lg font-bold text-white">2</span>
              </div>
              <h3 className="font-semibold text-sm sm:text-base mb-1">
                <EditableText sectionId="home-how-it-works" field="step2_title" defaultValue="Share" />
              </h3>
              <p className="text-gray-700 text-xs sm:text-sm">
                <EditableText sectionId="home-how-it-works" field="step2_desc" defaultValue="Send a link or QR" />
              </p>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-black flex items-center justify-center mx-auto mb-2 sm:mb-3">
                <span className="text-base sm:text-lg font-bold text-white">3</span>
              </div>
              <h3 className="font-semibold text-sm sm:text-base mb-1">
                <EditableText sectionId="home-how-it-works" field="step3_title" defaultValue="Done" />
              </h3>
              <p className="text-gray-700 text-xs sm:text-sm">
                <EditableText sectionId="home-how-it-works" field="step3_desc" defaultValue="Get what you want" />
              </p>
            </div>
          </div>
        </div>
      </EditableSection>

      {/* For Consumers Section */}
      <EditableSection sectionId="home-for-consumers" label="For Consumers" className={editMode ? "py-12 sm:py-16 bg-white" : "py-12 sm:py-16 bg-white"}>
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 items-center">
            <div className="text-center lg:text-left lg:pr-4">
              <span className="inline-block px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4">
                <EditableText sectionId="home-for-consumers" field="label" defaultValue="For Consumers" />
              </span>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-display font-bold text-gray-900 mb-3 sm:mb-4">
                <EditableText sectionId="home-for-consumers" field="title" defaultValue="Your preferences," className="inline" />{" "}
                <span className="bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500 bg-clip-text text-transparent">
                  <EditableText sectionId="home-for-consumers" field="title_highlight" defaultValue="everywhere you go" className="inline" />
                </span>
              </h2>
              <p className="text-gray-700 text-sm sm:text-base mb-6">
                <EditableText sectionId="home-for-consumers" field="description" defaultValue="Stop repeating yourself. Store your preferences once and share with any provider - from your hairstylist to your event host." />
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 max-w-lg mx-auto lg:mx-0">
                {[
                  { icon: FolderOpen, title: "Preference Management", desc: "All your profiles in one place - hair, nails, dietary, accessibility" },
                  { icon: Image, title: "Inspo Boards", desc: "Save and share visual inspiration with your providers" },
                  { icon: Calendar, title: "Experience Planning", desc: "RSVP to events with your preferences already attached" }
                ].map((feature, i) => (
                  <div key={i} className="flex flex-col items-center text-center p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-100">
                    <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-sm mb-3">
                      <feature.icon className="w-5 h-5 text-purple-600" />
                    </div>
                    <h3 className="font-semibold text-sm mb-1">{feature.title}</h3>
                    <p className="text-xs text-gray-600">{feature.desc}</p>
                  </div>
                ))}
              </div>
              
              <Button 
                onClick={() => navigate('/login?type=customer')}
                className="mt-6 rounded-full bg-purple-600 hover:bg-purple-700 text-sm"
                data-testid="consumer-get-started-btn"
              >
                Create Your Profile
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            
            {/* Consumer Visual - Preference Card Preview */}
            <div className="flex justify-center order-last lg:order-last lg:pl-4">
              <div className="bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl p-5 shadow-lg max-w-[320px]">
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center text-white font-semibold text-lg">
                      J
                    </div>
                    <div>
                      <p className="font-semibold text-sm text-gray-900">Jamie&apos;s Preference Card</p>
                      <p className="text-xs text-gray-500">Shareable with any provider</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="bg-purple-50 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <Scissors className="w-3.5 h-3.5 text-purple-600" />
                        <p className="text-xs text-purple-700 font-medium">Hair</p>
                      </div>
                      <p className="text-xs text-gray-700">4C texture, protective styles, no heat</p>
                    </div>
                    <div className="bg-pink-50 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <Heart className="w-3.5 h-3.5 text-pink-600" />
                        <p className="text-xs text-pink-700 font-medium">Accessibility</p>
                      </div>
                      <p className="text-xs text-gray-700">Fragrance-free products, sensory breaks</p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <PartyPopper className="w-3.5 h-3.5 text-amber-600" />
                        <p className="text-xs text-amber-700 font-medium">Events</p>
                      </div>
                      <p className="text-xs text-gray-700">Vegan, no shellfish, quiet seating</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-center gap-2 mt-4 pt-3 border-t border-gray-100">
                    <Share className="w-4 h-4 text-purple-500" />
                    <span className="text-xs text-purple-600 font-medium">Share with one tap</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </EditableSection>

      {/* For Hosts Section */}
      <EditableSection sectionId="home-for-hosts" label="For Hosts" className={editMode ? "py-12 sm:py-16 bg-gray-50" : "py-12 sm:py-16 bg-gray-50"}>
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 items-center">
            <div className="flex justify-center order-last lg:order-first lg:pr-4">
              <div className="bg-gradient-to-br from-amber-100 to-orange-100 rounded-2xl p-5 shadow-lg max-w-[320px]">
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-xl bg-black flex items-center justify-center">
                        <PartyPopper className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-sm">Host Dashboard</p>
                        <p className="text-xs text-gray-500">Your event OS</p>
                      </div>
                    </div>
                    <span className="text-[10px] font-medium px-2 py-0.5 bg-green-100 text-green-700 rounded-full">Live</span>
                  </div>
                  
                  <div className="space-y-2 mb-3">
                    <div className="flex items-center justify-between bg-gray-50 rounded-lg p-2.5">
                      <span className="text-xs text-gray-600">RSVPs collected</span>
                      <span className="text-sm font-semibold text-gray-900">147</span>
                    </div>
                    <div className="flex items-center justify-between bg-amber-50 rounded-lg p-2.5">
                      <span className="text-xs text-amber-700">Dietary needs flagged</span>
                      <span className="text-sm font-semibold text-amber-600">23</span>
                    </div>
                    <div className="flex items-center justify-between bg-blue-50 rounded-lg p-2.5">
                      <span className="text-xs text-blue-700">Accessibility requests</span>
                      <span className="text-sm font-semibold text-blue-600">8</span>
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-100 pt-3">
                    <p className="text-[10px] text-gray-400 mb-2">Integrates with</p>
                    <div className="flex items-center gap-1.5">
                      <div className="px-2 py-1 bg-gray-100 rounded text-[10px] font-medium text-gray-600">Partiful</div>
                      <div className="px-2 py-1 bg-gray-100 rounded text-[10px] font-medium text-gray-600">Posh</div>
                      <div className="px-2 py-1 bg-gray-100 rounded text-[10px] font-medium text-gray-600">Eventbrite</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="text-center lg:text-left order-first lg:order-last lg:pl-4">
              <span className="inline-block px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4">
                <EditableText sectionId="home-for-hosts" field="label" defaultValue="For Hosts" />
              </span>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-display font-bold text-gray-900 mb-3 sm:mb-4">
                <EditableText sectionId="home-for-hosts" field="title" defaultValue="Built for the people who" className="inline" />{" "}
                <span className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 bg-clip-text text-transparent">
                  <EditableText sectionId="home-for-hosts" field="title_highlight" defaultValue="make experiences happen" className="inline" />
                </span>
              </h2>
              <p className="text-gray-700 text-sm sm:text-base mb-6">
                <EditableText sectionId="home-for-hosts" field="description" defaultValue="Know your guests before they arrive. Collect preferences at RSVP and deliver personalized experiences at scale." />
              </p>
              
              <div className="grid grid-cols-2 gap-3 sm:gap-4 max-w-md mx-auto lg:mx-0">
                {[
                  { icon: Users, title: "Guest Insights", desc: "Dietary, accessibility, vibe preferences" },
                  { icon: LayoutDashboard, title: "Admin Dashboard", desc: "RSVPs, analytics, check-ins" },
                  { icon: Link2, title: "Connect Your Tools", desc: "Partiful, Posh, Eventbrite" },
                  { icon: Bell, title: "Smart Alerts", desc: "Get notified of special needs" }
                ].map((feature, i) => (
                  <div key={i} className="flex flex-col items-center text-center p-3 bg-white rounded-xl border border-amber-100 shadow-sm">
                    <div className="w-6 h-6 rounded bg-amber-50 flex items-center justify-center mb-2">
                      <feature.icon className="w-4 h-4 text-amber-600" />
                    </div>
                    <h3 className="font-medium text-sm">{feature.title}</h3>
                    <p className="text-xs text-gray-600">{feature.desc}</p>
                  </div>
                ))}
              </div>
              
              <Button 
                onClick={() => setShowEarlyAccessModal(true)}
                className="mt-6 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-sm"
                data-testid="host-get-started-btn"
              >
                Host an Event - Get Early Access
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </EditableSection>

      {/* Coming Soon Section - 3x3 Grid - Hidden for logged-in users */}
      {!isLoggedIn && (
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-block px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4">
              Coming Soon
            </span>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-display font-bold text-gray-900 mb-3 sm:mb-4">
              Your <span className="text-gray-500">[experience]</span> preferences,
              <br />perfectly communicated
            </h2>
            <p className="text-gray-700 text-sm sm:text-base mb-8">
              We are expanding to more verticals.<br />
              Share your preferences across all your IRL experiences.
            </p>
            
            {/* 3x3 Grid */}
            <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto">
              {[
                { catKey: "hair", name: "Hair", path: "/hair" },
                { catKey: "makeup", name: "Makeup", path: "/mua" },
                { catKey: "nails", name: "Nails", path: "/nail" },
                { catKey: "lashes", name: "Lash", path: "/lash" },
                { catKey: "events", name: "Events", path: "/events" },
                { catKey: "travel", name: "Travel", path: "/travel" },
                { catKey: "dispensary", name: "Dispensary", path: "/dispensaries" },
                { catKey: "style", name: "Retail", path: "/retail" },
                { catKey: "wellness", name: "Wellness", path: "/wellness" }
              ].map((item) => {
                const preset = getCategoryPreset(item.catKey);
                const Icon = preset?.icon || Sparkles;
                return (
                <Link 
                  key={item.name} 
                  to={item.path}
                  className="flex flex-col items-center p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors group"
                  data-testid={`coming-soon-${item.name.toLowerCase()}`}
                >
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-2 group-hover:scale-105 transition-transform" style={{ backgroundColor: preset?.bgColor }}>
                    <Icon className="w-6 h-6" style={{ color: preset?.iconColor }} />
                  </div>
                  <span className="text-sm font-medium text-gray-700">{item.name}</span>
                </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>
      )}

      {/* CTA Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
          <PhygitalIcon className="w-12 h-12 sm:w-14 sm:h-14 mx-auto mb-4 sm:mb-6" />
          <h2 className="text-xl sm:text-2xl md:text-3xl font-display font-bold text-gray-900 mb-3 sm:mb-4">
            Ready to simplify your experience?
          </h2>
          <p className="text-sm sm:text-base text-gray-700 mb-2">
            Store your preferences once and share them anywhere.
          </p>
          <p className="text-xs sm:text-sm text-gray-500 mb-6">
            Takes less than 2 minutes.
          </p>
          <div className="flex flex-row gap-3 justify-center">
            <Button 
              onClick={() => navigate('/login')}
              className="h-12 px-6 text-sm bg-black hover:bg-gray-800 text-white rounded-full"
            >
              Get Started Free
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button 
              onClick={() => navigate('/pricing')}
              variant="outline"
              className="h-12 px-6 text-sm rounded-full border-2"
            >
              View Pricing
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <SharedFooter />

      {/* Early Access Modal */}
      <EarlyAccessModal open={showEarlyAccessModal} onOpenChange={setShowEarlyAccessModal} />
    </div>
  );
};

export default HomePage;
